// 辣友會行動計劃——互動式逐幕簡報頁（/chilifans）
// 來源：docs/strategy/latyau-club-gtm.md v2.0；照 couponly /demo 哲學
// （FlowMap 故事地圖＋手機畫面自動輪播＋一句 caption＋BGM），
// 供辣谷主向持份者展示部署安排。內部簡報：robots Disallow、無 label（不入 popup 清單）。
import { useEffect, useMemo, useState } from 'react';
import { StoryStyles, PhoneFrame, BgmToggle } from '../components/GfStoryPhone';
import { chilifansScenes, type ChiliScene } from '../components/chilifansScenes';

const RED = '#d9381e';

const OWNER_META: Record<ChiliScene['owner'], { dot: string; label: string; cls: string }> = {
  auto: { dot: '🟢', label: '系統自動', cls: 'bg-green-500/15 text-green-300 ring-1 ring-green-500/30' },
  manual: { dot: '🟡', label: '手動營運', cls: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30' },
  new: { dot: '🔵', label: '需新建', cls: 'bg-blue-500/15 text-blue-300 ring-1 ring-blue-500/30' },
};

/** 故事地圖——12 幕一圖看清；可點跳幕（手動即停自動播放） */
function FlowMap({ scenes, active, onJump }: { scenes: ChiliScene[]; active: number; onJump: (i: number) => void }) {
  return (
    <div className="flex items-center justify-start gap-0 overflow-x-auto py-1 sm:justify-center">
      {scenes.map((s, i) => (
        <div key={`${s.node}-${i}`} className="flex shrink-0 items-center">
          <button onClick={() => onJump(i)} aria-label={s.node} className="flex w-[62px] flex-col items-center gap-1">
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full text-lg transition-all duration-300 ${
                i === active
                  ? 'demo-dot scale-110 text-white shadow-lg ring-4 ring-red-500/40'
                  : i < active
                    ? 'text-white'
                    : 'bg-white/5 text-stone-500 ring-1 ring-white/10'
              }`}
              style={i <= active ? { background: RED } : undefined}
            >
              {s.icon}
            </span>
            <span className={`text-center text-[9px] leading-tight ${i === active ? 'font-black' : 'text-stone-500'}`} style={i === active ? { color: RED } : undefined}>
              {s.node}
            </span>
          </button>
          {i < scenes.length - 1 && (
            <span className={`mx-0.5 h-[3px] w-3 rounded-full sm:w-6 ${i < active ? '' : 'bg-white/10'}`} style={i < active ? { background: RED } : undefined} />
          )}
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ kicker, title }: { kicker: string; title: string }) {
  return (
    <div className="mb-4">
      <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: RED }}>{kicker}</p>
      <h2 className="mt-0.5 text-xl font-black text-stone-100">{title}</h2>
    </div>
  );
}

export default function ChiliFans() {
  const [idx, setIdx] = useState(0);
  const [auto, setAuto] = useState(true);
  const scenes = useMemo(() => chilifansScenes(), []);
  const scene = scenes[Math.min(idx, scenes.length - 1)];
  const total = scenes.length;
  const owner = OWNER_META[scene.owner];

  useEffect(() => {
    if (!auto) return;
    const timer = window.setInterval(() => setIdx((i) => (i + 1) % total), 5400);
    return () => window.clearInterval(timer);
  }, [auto, total]);

  /** 一切手動導航＝停自動播放 */
  const jump = (i: number) => {
    setAuto(false);
    setIdx(Math.max(0, Math.min(total - 1, i)));
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-stone-200">
      <StoryStyles />
      <BgmToggle />

      <div className="mx-auto max-w-3xl px-4 py-10">
        {/* Hero */}
        <header className="text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-500">辣友會行動計劃 · GTM v2.0 · 內部策略簡報</p>
          <h1 className="mt-3 text-3xl font-black leading-tight text-stone-50 sm:text-4xl">
            🌶️ 辣友會——<span style={{ color: RED }}>香港辣文化的會籍</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-stone-400">
            這不是購買折扣，而是購買身份：每月辣中聖品驚喜、全港合作商家專屬優惠、結識同樣愛辣的朋友。
            <br className="hidden sm:block" />由食客變辣友，由辣友變辣文化的一部分。
            <span className="ml-1 font-bold text-stone-200">HK$99/年</span>
          </p>
          <div className="mx-auto mt-6 grid max-w-xl grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { n: '50', t: '準會員', d: '文記 83% 轉化實證' },
              { n: '104', t: '活躍對話', d: 'CRM 現成熱線' },
              { n: '8', t: '合作商家', d: '到店兌換網絡種子' },
              { n: '99', t: '元/年收款', d: 'KPay 已上線零摩擦' },
            ].map((s) => (
              <div key={s.t} className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                <p className="text-xl font-black" style={{ color: RED }}>{s.n}</p>
                <p className="text-[11px] font-bold text-stone-200">{s.t}</p>
                <p className="text-[9px] leading-tight text-stone-500">{s.d}</p>
              </div>
            ))}
          </div>
        </header>

        {/* 會員生命週期——逐幕自動播放 */}
        <section className="mt-12">
          <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 sm:p-6">
            <h2 className="font-bold text-stone-100">🎬 會員生命週期——一分鐘睇晒部署安排</h2>
            <p className="mt-1 text-sm text-stone-400">
              每一環都有現成引擎支撐，逐幕自動播放：
              <span className="ml-2 rounded-full bg-green-500/15 px-2 py-0.5 text-xs text-green-300 ring-1 ring-green-500/30">🟢 系統自動</span>
              <span className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-300 ring-1 ring-amber-500/30">🟡 手動營運</span>
              <span className="ml-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-xs text-blue-300 ring-1 ring-blue-500/30">🔵 需新建</span>
            </p>

            <div className="mt-4 rounded-xl bg-[#140f0f] p-2 ring-1 ring-white/5">
              <FlowMap scenes={scenes} active={idx} onJump={jump} />
            </div>

            <div className="mt-6 flex flex-col items-center gap-6 lg:flex-row lg:items-center lg:justify-center">
              <PhoneFrame key={idx} label={scene.phoneLabel}>
                {scene.phone}
              </PhoneFrame>

              <div className="w-full max-w-sm">
                <div className="flex items-center gap-3.5">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-3xl text-white shadow-lg" style={{ background: RED }}>
                    {scene.icon}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">會員生命週期</div>
                    <div className="truncate text-xl font-black text-stone-100">{scene.node}</div>
                  </div>
                </div>

                <p className="demo-pop mt-4 text-[15px] font-bold leading-relaxed text-stone-100" key={`cap-${idx}`}>
                  {scene.caption}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${owner.cls}`}>{owner.dot} {owner.label}</span>
                  <span className="text-[10px] text-stone-500">{scene.engine}</span>
                </div>

                <div className="mt-6 flex items-center gap-2">
                  <button
                    onClick={() => jump(idx - 1)}
                    disabled={idx === 0}
                    aria-label="上一幕"
                    className="rounded-xl bg-white/5 px-3.5 py-2 text-sm font-bold text-stone-300 ring-1 ring-white/10 disabled:opacity-30"
                  >
                    ◀
                  </button>
                  <button
                    onClick={() => setAuto((v) => !v)}
                    className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-black shadow transition"
                    style={auto ? { background: 'rgba(217,56,30,.15)', color: RED, boxShadow: `0 0 0 1px ${RED}55` } : { background: RED, color: '#fff' }}
                    aria-label={auto ? '暫停自動播放' : '開始自動播放'}
                  >
                    {auto ? '⏸' : '▶'}
                  </button>
                  <button
                    onClick={() => jump(idx + 1)}
                    disabled={idx === total - 1}
                    aria-label="下一幕"
                    className="rounded-xl bg-white/5 px-3.5 py-2 text-sm font-bold text-stone-300 ring-1 ring-white/10 disabled:opacity-30"
                  >
                    ▶
                  </button>
                  <div className="ms-2 h-1.5 flex-1 rounded-full bg-white/10">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%`, background: RED }} />
                  </div>
                  <span className="w-10 text-end font-mono text-xs text-stone-500" dir="ltr">{idx + 1}/{total}</span>
                </div>
              </div>
            </div>
            <p className="mt-4 text-center text-[11px] text-stone-500">🔇 右下角喇叭掣可開關背景音樂（瀏覽器限制：需要你點擊一次先會有聲）。</p>
          </div>
        </section>

        {/* 三階段引爆路徑 */}
        <section className="mt-12">
          <SectionTitle kicker="Ignition Path" title="三階段引爆路徑" />
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              {
                phase: 'Phase 1', name: '種子期', days: '第 1–30 日', target: '60–100 付費會員',
                color: RED,
                moves: ['早鳥專屬窗口（比公開早 7 日）', '創會會員 100 席＋永久鎖定 99 元', '文記社交證據做招募素材', '「下次優先」8 人承諾兌現', 'KPay 一鍵入會零人手'],
                note: '訊息語氣是「邀請」而非「促銷」——這批人是元老。',
              },
              {
                phase: 'Phase 2', name: '擴散期', days: '第 31–90 日', target: '150–200 付費會員',
                color: '#F4A261',
                moves: ['推薦入會回贈 HK$20＋朋友減 10', '季度辣任務 2.0（#辣友會 公開承諾）', '每月會員專屬兌換活動', '季度聚會：會員帶一位朋友'],
                note: '推薦不是抽傭那麼冷，是「帶你入圈」的社交行為。',
              },
              {
                phase: 'Phase 3', name: '城市引爆', days: '第 91 日起', target: '250–350 會員 · 商家 8→80',
                color: '#FFD700',
                moves: ['享辣 IP 孵化（10 個辣界 KOL）', '每季嘗新活動：試食＋聯乘＋辣度挑戰', '用文記／華星案例擴張商家網絡', '會員卡＝全港辣店通行證'],
                note: '辣友會＝香港辣文化的代名詞。',
              },
            ].map((p) => (
              <div key={p.phase} className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="flex items-baseline justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: p.color }}>{p.phase} · {p.days}</p>
                </div>
                <h3 className="mt-1 text-lg font-black text-stone-100">{p.name}</h3>
                <p className="text-[11px] font-bold" style={{ color: p.color }}>🎯 {p.target}</p>
                <ul className="mt-3 space-y-1.5">
                  {p.moves.map((m) => (
                    <li key={m} className="flex gap-1.5 text-[11px] leading-snug text-stone-300"><span style={{ color: p.color }}>▸</span>{m}</li>
                  ))}
                </ul>
                <p className="mt-3 border-t border-white/10 pt-2 text-[10px] italic leading-snug text-stone-500">{p.note}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 三大情感支柱 */}
        <section className="mt-12">
          <SectionTitle kicker="Psychology" title="三大情感支柱" />
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { icon: '🪪', name: '身份認同', en: 'Unity（歸屬感）', body: '「我們辣友」的二元思維——會員專屬連結、CRM 稱呼「辣友 {name}」、包容性語言。' },
              { icon: '⏳', name: '稀缺性', en: 'Scarcity', body: '限量 100 席創會會員、永久鎖定 99 元、每次活動會員優先窗口 48 小時——怕失去比渴望獲得更強。' },
              { icon: '🤝', name: '歸屬感', en: 'Unity + Liking', body: '會員 WhatsApp 群組、季度辣友聚會——線上旅程帶到線下見面，相似性原則建立紐帶。' },
            ].map((p) => (
              <div key={p.name} className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                <p className="text-2xl">{p.icon}</p>
                <h3 className="mt-1 text-base font-black text-stone-100">{p.name}</h3>
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{p.en}</p>
                <p className="mt-2 text-[11px] leading-relaxed text-stone-400">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 核心訊息 */}
        <section className="mt-12">
          <div className="rounded-2xl p-5 text-center ring-1 ring-white/10" style={{ background: `linear-gradient(135deg, ${RED}22, transparent)` }}>
            <p className="text-lg font-black leading-snug text-stone-100">
              「辣友會不是一個新系統，<br className="sm:hidden" />是<span style={{ color: RED }}>現有系統的會員化包裝</span>。」
            </p>
            <p className="mt-2 text-[11px] text-stone-500">支付／旅程／核銷／推薦／回購——12 環裡 10 環由現成引擎自動跑，新建僅 /chiliclub 落地頁一項。</p>
          </div>
        </section>

        {/* 30 日行動清單 */}
        <section className="mt-12">
          <SectionTitle kicker="30-Day Checklist" title="30 日行動清單（零新開發優先）" />
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              { n: 1, g: 'funnel', t: '辣友會落地頁 /chiliclub', e: 'KPay Gateway＋landing 模式' },
              { n: 2, g: 'funnel', t: '種子池招募訊息（文記版＋活躍對話版）', e: '批量腳本＋autoStore' },
              { n: 3, g: 'funnel', t: '「下次優先」8 人專屬訊息', e: '客戶標籤＋test-send' },
              { n: 4, g: 'funnel', t: '入會歡迎旅程（歡迎→首送預告→群組邀請）', e: 'godfather 歡迎旅程複用' },
              { n: 5, g: 'fulfil', t: '辣中聖品首月配送', e: '郵寄引擎＋Label 列印' },
              { n: 6, g: 'community', t: '文記社交證據內容包', e: '手動＋FB/IG' },
              { n: 7, g: 'community', t: '會員專屬 WhatsApp 群組開組', e: '群組 JID 接入 notify' },
              { n: 8, g: 'community', t: '本月優惠速遞模板', e: 'CMS 模板＋批量腳本' },
              { n: 9, g: 'expand', t: '首個會員專屬兌換活動（2–3 商家試點）', e: 'godfather 引擎＋核銷即派券' },
              { n: 10, g: 'expand', t: '商家招募 pitch deck（文記／華星數據案例）', e: '手動文檔' },
            ].map((a) => {
              const G = {
                funnel: { c: RED, t: '入會漏斗' },
                fulfil: { c: '#F4A261', t: '履約承諾' },
                community: { c: '#FFD700', t: '社群感' },
                expand: { c: '#a78bfa', t: '擴張起點' },
              }[a.g as 'funnel' | 'fulfil' | 'community' | 'expand'];
              return (
                <div key={a.n} className="flex items-start gap-2.5 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[11px] font-black text-white" style={{ background: G.c }}>{a.n}</span>
                  <div className="min-w-0">
                    <p className="text-[12px] font-bold leading-snug text-stone-200">{a.t}</p>
                    <p className="text-[10px] text-stone-500"><span style={{ color: G.c }}>{G.t}</span> · {a.e}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-stone-500">
            優先序：#1–4 沒有就沒有收入；#5 沒有就沒有信任；#6–8 是辣友會的靈魂；#9–10 是擴張起點。
          </p>
        </section>

        {/* 目標與指標 */}
        <section className="mt-12">
          <SectionTitle kicker="North Star" title="目標與指標" />
          <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
            <p className="text-[12px] text-stone-400">北極星指標：<span className="font-bold text-stone-100">付費辣友會會員數</span>（已繳費＋期內有提取／核銷）</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[420px] text-left text-[11px]">
                <thead>
                  <tr className="border-b border-white/10 text-stone-500">
                    <th className="pb-1.5 font-medium">時點</th>
                    <th className="pb-1.5 font-medium">付費會員</th>
                    <th className="pb-1.5 font-medium">合作商家</th>
                    <th className="pb-1.5 font-medium">里程碑</th>
                  </tr>
                </thead>
                <tbody className="text-stone-300">
                  {[
                    ['30 日', '60–100', '10→12', '種子池轉化為主'],
                    ['60 日', '150–200', '15–20', '裂變引擎啟動'],
                    ['90 日', '250–350', '25–30', '首次聚會＋嘗新活動'],
                  ].map((r) => (
                    <tr key={r[0]} className="border-b border-white/5 last:border-0">
                      <td className="py-1.5 font-bold">{r[0]}</td>
                      <td className="py-1.5 font-black" style={{ color: RED }}>{r[1]}</td>
                      <td className="py-1.5">{r[2]}</td>
                      <td className="py-1.5 text-stone-500">{r[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
              {[
                ['招募轉化', '≥25%'], ['首月提取', '≥90%'], ['次月留存', '≥80%'], ['推薦率', '≥15%'], ['商家核銷', '≥40%'],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-white/5 p-2 text-center ring-1 ring-white/5">
                  <p className="text-[9px] text-stone-500">{k}</p>
                  <p className="text-[13px] font-black text-stone-200">{v}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-stone-500">營收參考：300 會員 × 99 = <span className="font-bold text-stone-300">HK$29,700</span>/年費＋商家導流價值＝「現金流＋網絡擴張」雙引擎。</p>
          </div>
        </section>

        {/* 倫理紅線＋Footer */}
        <section className="mt-10">
          <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
            <p className="text-[11px] font-bold text-stone-300">📏 倫理紅線（三條，絕不觸碰）</p>
            <ul className="mt-1.5 space-y-1 text-[10px] leading-relaxed text-stone-500">
              <li>① 訊息有節奏（每月／季度），不做每日轟炸式時間黑洞</li>
              <li>② 目標用戶 18+；招募訊息不含紅點＋倒數組合</li>
              <li>③「十倍奉還」以真實數據計算（辣中聖品＋商家優惠 &gt; 990 元）；不虛構任何數字</li>
            </ul>
          </div>
          <p className="mt-8 text-center text-xs text-stone-600">香港🌶️辣谷 HKSpicyValley.com · 內部策略簡報 v2.0 · 2026-10-04</p>
        </section>
      </div>
    </div>
  );
}
